// AuthContext.tsx - גרסה משופרת
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { auth, db, getFirebaseFunctions } from '../firebaseConfig';
import { 
  onAuthStateChanged, 
  getIdTokenResult, 
  User, 
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import { invalidateReportsBaseDataCache } from './reportDataCache';
import {
  clearDashboardStatsSessionCache,
  invalidateDashboardStatsCache,
} from './dashboardStatsCache';
import { setMessagingAccessBlocked } from './messagingAccess';
import { messagingService } from './MessagingService';
import { CircularProgress, Box } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import {
  auditUserFromAuth,
  buildLoginAudit,
  buildLogoutAudit,
  clearLoginLoggedThisSession,
  hasLoggedLoginThisSession,
  logAuditActivity,
  markLoginLoggedThisSession,
  setAuditActorContext,
} from './activityAuditLog';

// User role types
export type UserRole =
  | 'admin'
  | 'service'
  | 'technician'
  | 'viewer'
  | 'field_agent'
  | 'external_viewer'
  | 'warehouse'
  /** עזב את החברה — החשבון נעול להתחברות, הרשומה וההיסטוריה נשמרות */
  | 'former_employee'
  | null;

interface AuthContextType {
  user: User | null;
  role: UserRole;
  isAdmin: boolean;
  isManager: boolean;
  isTechnician: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  refreshToken: () => Promise<void>;
  logout: () => Promise<void>;
  updateUserRole: (newRole: UserRole) => Promise<void>;
  updateProfilePhoto: (url: string) => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  isAdmin: false,
  isManager: false,
  isTechnician: false,
  isAuthenticated: false,
  loading: true,
  refreshToken: async () => {},
  logout: async () => {},
  updateUserRole: async () => {},
  updateProfilePhoto: async () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const externalStoreNameRef = useRef<string>('');
  const roleRef = useRef<UserRole>(null);
  const userRef = useRef<User | null>(null);
  /** מונע bootstrap כפול כש־updateProfile מפעיל שוב onAuthStateChanged (גורם ל־Firestore ca9/b815) */
  const authBootstrapUidRef = useRef<string | null>(null);
  const authSideEffectsTimerRef = useRef<number | null>(null);

  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const isExternal = role === 'external_viewer';
    setAuditActorContext({
      actorType: isExternal ? 'external' : 'employee',
      actorRole: role || undefined,
      storeName: isExternal ? externalStoreNameRef.current || undefined : undefined,
    });
  }, [role]);

  useEffect(() => {
    return () => {
      if (authSideEffectsTimerRef.current != null) {
        window.clearTimeout(authSideEffectsTimerRef.current);
        authSideEffectsTimerRef.current = null;
      }
    };
  }, []);

  // גישה מורחבת לעדכון תפקיד משתמש ב-Firestore ו-Token
  const updateUserRole = async (newRole: UserRole) => {
    if (role === 'external_viewer') {
      throw new Error('External viewers cannot change roles');
    }
    if (user && newRole) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, { 
          role: newRole,
          updatedAt: new Date()
        }, { merge: true });
        
        setRole(newRole);
        
        // קרא לפונקצית Cloud ששולחת עדכון Custom Claims
        // העדכון בשרת משום שלא ניתן לעשות זאת בצד לקוח
        try {
          // אם יש לך callable function לעדכון תפקידים
          const setUserRoleFunction = httpsCallable(getFirebaseFunctions(), 'setUserRole');
          await setUserRoleFunction({ uid: user.uid, role: newRole });
        } catch (functionError) {
          console.error('שגיאה בקריאה לפונקצית ענן:', functionError);
        }
        
        // נסה לרענן את הטוקן 
        await refreshToken();
      } catch (error) {
        console.error('שגיאה בעדכון תפקיד משתמש:', error);
        throw error;
      }
    }
  };
  
  const syncCustomClaimsForRole = async (targetUser: User, newRole: UserRole) => {
    if (newRole === 'external_viewer' || newRole === 'admin' || newRole === 'former_employee') return;
    try {
      const setUserRoleFunction = httpsCallable(getFirebaseFunctions(), 'setUserRole');
      await setUserRoleFunction({ uid: targetUser.uid, role: newRole });
      await targetUser.getIdToken(true);
    } catch (functionError) {
      console.error('שגיאה בסנכרון Custom Claims:', functionError);
    }
  };

  // פונקצית רענון טוקן משופרת
  const refreshToken = async () => {
    if (user) {
      try {
        await user.getIdToken(true);
        const tokenResult = await getIdTokenResult(user, false);
        const tokenRole = tokenResult.claims.role as UserRole;

        // אותו סדר כמו בהתחברות: משתמש חיצוני נשאר צופה חיצוני גם אם יש לו טוקן/רשומה ב-users
        const externalSnap = await getDoc(doc(db, 'externalUsers', user.uid));
        if (externalSnap.exists() || tokenRole === 'external_viewer') {
          setRole('external_viewer');
          return;
        }

        const userDocRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userDocRef);
        const firestoreRole = String(userSnap.exists() ? userSnap.data()?.role || '' : '').trim() as UserRole;
        if (firestoreRole) {
          setRole(firestoreRole);
          if (firestoreRole !== tokenRole) await syncCustomClaimsForRole(user, firestoreRole);
          return;
        }

        if (tokenRole) {
          setRole(tokenRole);
          return;
        }

        console.warn('לא נמצא תפקיד ב-Firestore');
      } catch (error) {
        console.error('שגיאה ברענון טוקן:', error);
      }
    }
  };

  // פונקצית התנתקות
  const logout = async () => {
    try {
      const currentUser = userRef.current;
      const currentRole = roleRef.current;
      if (currentUser?.uid) {
        const isExternal = currentRole === 'external_viewer';
        logAuditActivity(auditUserFromAuth(currentUser), buildLogoutAudit({
          actorType: isExternal ? 'external' : 'employee',
          actorRole: currentRole || undefined,
          storeName: isExternal ? externalStoreNameRef.current || undefined : undefined,
        }));
        clearLoginLoggedThisSession(currentUser.uid);
      }
      invalidateReportsBaseDataCache();
      invalidateDashboardStatsCache();
      clearDashboardStatsSessionCache();
      setAuditActorContext({ actorType: 'employee' });
      externalStoreNameRef.current = '';
      authBootstrapUidRef.current = null;
      await signOut(auth);
      setUser(null);
      setRole(null);
    } catch (error) {
      console.error('שגיאה בהתנתקות:', error);
    }
  };

  // עדכון תמונת פרופיל (שימוש גם בהודעות ובמקומות נוספים)
  const updateProfilePhoto = async (url: string) => {
    if (!user) return;
    try {
      await updateProfile(user, { photoURL: url });
      // משתמשים חיצוניים – עדכון רק ב-externalUsers; עובדים – ב-users
      const targetCollection = role === 'external_viewer' ? 'externalUsers' : 'users';
      await setDoc(doc(db, targetCollection, user.uid), { photoURL: url }, { merge: true });
      setUser((prev) => (prev ? { ...prev, photoURL: url } : null));
    } catch (error) {
      console.error('שגיאה בעדכון תמונת פרופיל:', error);
      throw error;
    }
  };

  // האזן לשינויי מצב אימות
  useEffect(() => {
    setLoading(true);

    const schedulePostAuthSideEffects = (fn: () => void) => {
      if (authSideEffectsTimerRef.current != null) {
        window.clearTimeout(authSideEffectsTimerRef.current);
      }
      // דוחים כתיבות/לוגים אחרי שה־WebChannel של Firestore מתייצב (מונע ca9/b815)
      authSideEffectsTimerRef.current = window.setTimeout(() => {
        authSideEffectsTimerRef.current = null;
        try {
          fn();
        } catch (sideEffectError) {
          console.warn('Auth post-login side effect skipped:', sideEffectError);
        }
      }, 750);
    };

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        if (authSideEffectsTimerRef.current != null) {
          window.clearTimeout(authSideEffectsTimerRef.current);
          authSideEffectsTimerRef.current = null;
        }
        authBootstrapUidRef.current = null;
        setMessagingAccessBlocked(false);
        setUser(null);
        setRole(null);
        setLoading(false);
        return;
      }

      // updateProfile / רענון טוקן מפעילים onAuthStateChanged שוב — בלי bootstrap חוזר
      if (authBootstrapUidRef.current === currentUser.uid) {
        setUser(currentUser);
        return;
      }

      let tokenRole: UserRole | null = null;
      try {
        setUser(currentUser);
        await currentUser.getIdToken(false);
        const tokenResult = await getIdTokenResult(currentUser, false);
        let currentRole = (tokenResult.claims.role as UserRole) || null;
        tokenRole = currentRole;

        // קודם כל: בדיקה אם משתמש חיצוני (נשמר רק ב-externalUsers) – לא נוגעים ב-users בכלל
        const externalRef = doc(db, 'externalUsers', currentUser.uid);
        const externalSnap = await getDoc(externalRef);
        if (externalSnap.exists() || currentRole === 'external_viewer') {
          currentRole = 'external_viewer';
          setMessagingAccessBlocked(true);
          const externalData = externalSnap.exists() ? externalSnap.data() : null;
          if (externalData && externalData.active === false) {
            try {
              sessionStorage.setItem('loginInactiveExternalUser', '1');
            } catch {
              /* ignore */
            }
            authBootstrapUidRef.current = null;
            await signOut(auth);
            setUser(null);
            setRole(null);
            setLoading(false);
            return;
          }
          const storeName = String(externalData?.storeName || '').trim();
          const displayName = String(
            externalData?.username || externalData?.fullName || currentUser.displayName || '',
          ).trim();
          externalStoreNameRef.current = storeName;
          setAuditActorContext({
            actorType: 'external',
            actorRole: 'external_viewer',
            storeName: storeName || undefined,
          });

          // מסמנים לפני side-effects כדי ש־updateProfile לא יריץ bootstrap שוב
          authBootstrapUidRef.current = currentUser.uid;
          setRole(currentRole);
          setLoading(false);

          schedulePostAuthSideEffects(() => {
            if (displayName && displayName !== String(currentUser.displayName || '').trim()) {
              void updateProfile(currentUser, { displayName }).catch((nameSyncError) => {
                console.warn('External displayName sync skipped:', nameSyncError);
              });
            }
            if (externalSnap.exists()) {
              void setDoc(externalRef, { lastLogin: new Date() }, { merge: true }).catch((loginWriteError) => {
                console.warn('external lastLogin Firestore update skipped:', loginWriteError);
              });
            }
            if (!hasLoggedLoginThisSession(currentUser.uid)) {
              markLoginLoggedThisSession(currentUser.uid);
              logAuditActivity(
                {
                  uid: currentUser.uid,
                  displayName: displayName || currentUser.displayName,
                  email: currentUser.email,
                },
                buildLoginAudit({
                  actorType: 'external',
                  actorRole: 'external_viewer',
                  storeName: storeName || undefined,
                }),
              );
            }
          });
          return;
        }

        // מכאן – רק עובדים פנימיים: עבודה עם קולקציית users
        let preferredDisplayName = '';
        let employeeRoleForLog: string | undefined = currentRole || undefined;
        /** הרשומה נקראה בהצלחה וקיימת אך בלי role — רק אז מותר לכתוב ברירת מחדל ל-DB */
        let userDocExistsWithoutRole = false;
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userDocRef);

          if (userSnap.exists()) {
            if (!String(userSnap.data()?.role || '').trim()) userDocExistsWithoutRole = true;
            const userData = userSnap.data() || {};
            preferredDisplayName = String(
              userData.fullName || userData.name || userData.displayName || '',
            ).trim();
            // התפקיד ברשומת העובד (רק מנהל יכול לשנות אותו) קובע; token ישן מסונכרן אליו
            const firestoreRole = String(userData.role || '').trim() as UserRole;
            if (firestoreRole && firestoreRole !== currentRole) {
              currentRole = firestoreRole;
              void syncCustomClaimsForRole(currentUser, firestoreRole);
            }
            employeeRoleForLog = String(currentRole || userData.role || '') || undefined;
            externalStoreNameRef.current = '';
            setAuditActorContext({
              actorType: 'employee',
              actorRole: employeeRoleForLog,
            });
          } else {
            // getDoc עלול להחזיר "לא קיים" מהמטמון המקומי בזמן ניתוק — מוודאים מול השרת
            // לפני יצירה, אחרת setDoc (בלי merge) דורס עובד קיים לצופה
            const serverSnap = await getDocFromServer(userDocRef);
            if (serverSnap.exists()) {
              throw new Error('users doc exists on server but was missing from local cache');
            }
            // עובד פנימי חדש – צור רשומה ב-users (fullName כי הטבלה מצפה לזה)
            const defaultRole = currentRole || 'viewer';
            await setDoc(userDocRef, {
              fullName: currentUser.displayName || '',
              email: currentUser.email || '',
              role: defaultRole,
              phone: '',
              createdAt: new Date(),
              updatedAt: new Date(),
              lastLogin: new Date(),
            });
            currentRole = defaultRole;
            employeeRoleForLog = defaultRole;
            void syncCustomClaimsForRole(currentUser, defaultRole);
            externalStoreNameRef.current = '';
            setAuditActorContext({
              actorType: 'employee',
              actorRole: defaultRole,
            });
          }
        } catch (firestoreError) {
          console.error('שגיאה בקריאה מ-Firestore:', firestoreError);
        }

        if (!currentRole) {
          // כשל בקריאה (רשת / Firestore לא זמין) לא נכתב ל-DB — אחרת כתיבה שממתינה
          // בתור האופליין מורידה טכנאי לצופה לצמיתות כשהחיבור חוזר
          currentRole = 'viewer';
          if (userDocExistsWithoutRole) {
            try {
              await setDoc(doc(db, 'users', currentUser.uid), { role: currentRole }, { merge: true });
            } catch (err) {
              console.error('שגיאה בעדכון תפקיד ברירת מחדל:', err);
            }
          }
        }

        if (currentRole === 'former_employee') {
          try {
            sessionStorage.setItem('loginFormerEmployee', '1');
          } catch {
            /* ignore */
          }
          authBootstrapUidRef.current = null;
          await signOut(auth);
          setUser(null);
          setRole(null);
          setLoading(false);
          return;
        }

        authBootstrapUidRef.current = currentUser.uid;
        setRole(currentRole);
        setMessagingAccessBlocked(false);
        setLoading(false);

        schedulePostAuthSideEffects(() => {
          const authDisplayName = String(currentUser.displayName || '').trim();
          if (preferredDisplayName && preferredDisplayName !== authDisplayName) {
            void updateProfile(currentUser, { displayName: preferredDisplayName }).catch((nameSyncError) => {
              console.error('שגיאה בסנכרון שם תצוגה מהמשתמש ב-Firestore:', nameSyncError);
            });
          }
          void setDoc(doc(db, 'users', currentUser.uid), { lastLogin: new Date() }, { merge: true }).catch(
            (loginWriteError) => {
              console.warn('lastLogin Firestore update skipped:', loginWriteError);
            },
          );
          if (!hasLoggedLoginThisSession(currentUser.uid)) {
            markLoginLoggedThisSession(currentUser.uid);
            logAuditActivity(
              {
                uid: currentUser.uid,
                displayName: preferredDisplayName || currentUser.displayName || currentUser.email,
                email: currentUser.email,
              },
              buildLoginAudit({
                actorType: 'employee',
                actorRole: employeeRoleForLog || currentRole || undefined,
              }),
            );
          }
        });
      } catch (error) {
        console.error('שגיאה בקבלת תפקיד משתמש:', error);
        // תפקיד זמני לסשן בלבד — לא נכתב ל-DB (התפקיד האמיתי נשאר כפי שהמנהל הגדיר)
        let fallbackRole: UserRole = tokenRole && tokenRole !== 'external_viewer' ? tokenRole : 'viewer';
        try {
          const externalSnap = await getDoc(doc(db, 'externalUsers', currentUser.uid));
          if (externalSnap.exists()) fallbackRole = 'external_viewer';
        } catch (_) {}
        if (tokenRole === 'external_viewer') fallbackRole = 'external_viewer';
        setMessagingAccessBlocked(fallbackRole === 'external_viewer');
        setRole(fallbackRole);
        authBootstrapUidRef.current = currentUser.uid;
        setLoading(false);
      }
    });

    // נקה האזנה כשהקומפוננטה מתפרקת
    return () => {
      if (authSideEffectsTimerRef.current != null) {
        window.clearTimeout(authSideEffectsTimerRef.current);
        authSideEffectsTimerRef.current = null;
      }
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const blocked = role === 'external_viewer';
    setMessagingAccessBlocked(blocked);
    if (blocked) {
      messagingService.unsubscribeAll();
    }
  }, [role]);

  // ערכי הקונטקסט
  const contextValue: AuthContextType = {
    user,
    role,
    isAdmin: role === 'admin',
    isManager: role === 'admin' || role === 'service',
    isTechnician: role === 'technician' || role === 'admin',
    isAuthenticated: !!user,
    loading,
    refreshToken,
    logout,
    updateUserRole,
    updateProfilePhoto,
  };

  // הצג מסך טעינה
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => useContext(AuthContext);